import { useForm } from '@nerdgeschoss/react-use-form-library';
import { sortBy } from 'lodash';
import React, { useEffect } from 'react';
import { FormCheckbox } from '../../../../components/Form/Checkbox/FormCheckbox';
import { FormDatepicker } from '../../../../components/Form/Datepicker/FormDatepicker';
import { FormImageUpload } from '../../../../components/Form/FileUpload/FormImageUpload';
import { FormForm } from '../../../../components/Form/FormForm';
import { FormNumberField } from '../../../../components/Form/NumberField/FormNumberField';
import { FormSelect } from '../../../../components/Form/Select/FormSelect';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import GlobalProps from '../../../../components/globalProps';
import { Line } from '../../../../components/Line/Line';
import { Stack } from '../../../../components/Stack/Stack';
import { t } from '../../../../locales/translate';
import { handleError } from '../../../../model/notification';
import { applyTax } from '../../../../model/price';
import { addProductPrice, getProduct, PriceUpdate, updateProductPrice } from '../../../../model/product';
import { Shop } from '../../../../model/shop';
import { round } from '../../../../util/values';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

interface Props extends GlobalProps {
  productId: string;
  price: PriceUpdate | null;
  shops: Shop[];
  vatRate?: number;
  onClose?: () => void;
}

export function PricingUpdateForm({ productId, price, onClose, vatRate, shops }: Props): JSX.Element | null {
  const newRecord = !price;
  const { fields, changes, onSubmit, valid, dirty, submissionStatus } = useForm({
    model: { ...price },
    validations: {
      amount: ['required'],
      activeFrom: newRecord ? ['required'] : undefined,
    },
    onSubmit: async () => {
      try {
        if (price) {
          await updateProductPrice(price.id, changes);
        } else {
          await addProductPrice(productId, changes);
        }
        await getProduct(productId);
        onClose?.();
      } catch (error) {
        handleError(error);
      }
    },
  });

  useEffect(() => {
    if (newRecord) {
      fields.activeFrom.onChange(new Date());
    }
  }, []);

  const shopOptions = sortBy(
    shops.map((e) => ({ value: e.id, label: e.name })),
    (e) => e.label
  );

  return (
    <FormForm
      onClose={onClose}
      onSubmit={onSubmit}
      canSubmit={dirty && valid}
      loading={submissionStatus === 'submitting'}
    >
      <Stack>
        {newRecord && (
          <>
            <Line equalWidth>
              <FormCheckbox
                value={!fields.shopId.value}
                onChange={(independent) => {
                  if (independent) {
                    fields.shopId.onChange(null);
                  } else {
                    fields.shopId.onChange(shopOptions[0].value);
                  }
                }}
                label={t.model.shop.shopIndependent}
              />
              <Collapse open={!!fields.shopId.value}>
                {() => <FormSelect {...fields.shopId} options={shopOptions} label={t.model.shop._name} required />}
              </Collapse>
            </Line>
            <Line equalWidth>
              <FormDatepicker {...fields.activeFrom} label={t.model.datetime.startDate} required hourpicker />
              <FormDatepicker {...fields.activeUntil} label={t.model.datetime.endDate} hourpicker />
            </Line>
          </>
        )}
        <Line>
          <FormNumberField {...fields.amount} label={t.model.price.netPrice} step={0.000001} />
          <FormNumberField
            value={fields.amount.value && vatRate && round(applyTax(fields.amount.value ?? 0, vatRate), 2)}
            required
            disabled
            step={0.01}
            label={t.model.price.brutPrice}
          />
        </Line>
        <Line align="top">
          <FormNumberField
            {...fields.originalPrice}
            step={0.000001}
            label={t.model.price.originalPrice}
            description={t.model.price.originalPriceDescription}
          />
          <FormNumberField
            value={
              vatRate && fields.originalPrice.value && round(applyTax(fields.originalPrice.value ?? 0, vatRate), 2)
            }
            required
            disabled
            step={0.01}
            label={t.model.price.originalPriceBrut}
          />
        </Line>
        {!newRecord && (
          <>
            <FormImageUpload {...fields.overlayDetailImage} label={t.model.product.overlayDetail} />
            <FormImageUpload {...fields.overlayListingImage} label={t.model.product.overlayListing} />
          </>
        )}
        <FormTextField {...fields.comment} label={t.model.price.comment} />
      </Stack>
    </FormForm>
  );
}
